/**
 * "Show me how to …", planned by a connected model and checked before it runs.
 *
 * The model is given the menu (every screen this person may open, with its
 * one line) and the controls visible on the current screen, and asked for a
 * short walkthrough as JSON. What comes back is treated as a proposal:
 *
 *  - a route not in the menu, or above the person's level, is dropped;
 *  - a target is only ever words to look for on the page — it cannot name a
 *    selector or a script, and the director still decides what may be pressed;
 *  - a sentence carrying a figure that is not in what the model was shown is
 *    dropped, since a walkthrough is not where a number gets invented;
 *  - the whole plan is capped at eight steps.
 *
 * Without a model, or when nothing survives, the caller falls back to the
 * deterministic tours — the guide, the screen's own parts.
 */
import { verifyFigures } from "../../domain/decision";
import { labelOf, SCREEN_BY_ROUTE, type NavLayout } from "../../layout/navigation";
import { whatOf } from "../../layout/suites";
import { canSee, type AccessLevel } from "../access";
import { askModel, hasModel, type ExternalConfig } from "../external";
import { isVisible, nameOf } from "./anchors";
import type { Step } from "./director";

const PLAN_PROMPT = [
  "You are Sky, a guide inside an airfield operations platform. You show people how to do things by pointing at the screen.",
  "You are given the screens they can open and the controls visible on the screen they are on.",
  'Reply with JSON only: {"steps":[{"route":"<a route from the list, optional>","target":"<the exact words of a control or heading, optional>","action":"point|press","text":"<one or two short sentences>"}]}.',
  "At most 8 steps. Use only routes from the list and words that appear on screen. Use press only for moving between screens or tabs, never for saving, approving or changing a record — the person does that.",
  "Do not state numbers. If the platform cannot do what is asked, reply with a single step saying so.",
].join(" ");

/** The visible controls and headings of the current screen, by name. */
export function visibleControls(max = 60): string[] {
  const main = document.querySelector("[data-sky='main']") ?? document.body;
  const names: string[] = [];
  const seen = new Set<string>();
  for (const el of Array.from(main.querySelectorAll<HTMLElement>("button, a[href], [role='tab'], h1, h2, h3, label, input[placeholder]"))) {
    if (names.length >= max) break;
    if (!isVisible(el)) continue;
    const n = nameOf(el).slice(0, 60);
    if (n.length < 2 || seen.has(n.toLowerCase())) continue;
    seen.add(n.toLowerCase());
    names.push(n);
  }
  return names;
}

export async function planWithModel(
  cfg: ExternalConfig,
  question: string,
  layout: NavLayout,
  level: AccessLevel,
  route: string,
  signal?: AbortSignal,
): Promise<{ steps: Step[]; model: string } | null> {
  if (!hasModel(cfg)) return null;
  const screens = layout.groups
    .flatMap((g) => g.screens)
    .filter((to) => SCREEN_BY_ROUTE.has(to) && !layout.hidden.includes(to) && canSee(level, to));
  const menu = screens.map((to) => `${to} — ${labelOf(layout, to)}: ${whatOf(to)}`).join("\n");
  const controls = visibleControls();
  const material = `Screens:\n${menu}\n\nThey are on ${route}. Visible there:\n${controls.join(" | ")}\n\nThey asked: ${question}`;
  const figures = new Set<string>((material.match(/\d[\d,]*(?:\.\d+)?/g) ?? []).map((x) => x.replace(/,/g, "")));
  const allowed = new Set(screens);

  let text: string;
  let model: string;
  try {
    const reply = await askModel(cfg, PLAN_PROMPT, material, signal);
    text = reply.text;
    model = reply.model;
  } catch {
    return null;
  }
  const json = text.match(/\{[\s\S]*\}/)?.[0];
  if (!json) return null;
  let parsed: { steps?: { route?: unknown; target?: unknown; action?: unknown; text?: unknown }[] };
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  const steps: Step[] = [];
  for (const s of (parsed.steps ?? []).slice(0, 8)) {
    const line = typeof s.text === "string" ? s.text.trim().slice(0, 300) : "";
    if (!line || verifyFigures(line, figures).length > 0) continue;
    const step: Step = { text: line, title: "How to" };
    if (typeof s.route === "string") {
      const r = s.route.split("?")[0];
      if (!allowed.has(r)) continue;
      step.route = r;
      step.title = labelOf(layout, r);
    }
    if (typeof s.target === "string" && s.target.trim()) {
      step.target = { text: s.target.trim().slice(0, 80) };
      if (s.action === "press") step.action = "press";
    }
    steps.push(step);
  }
  return steps.length ? { steps, model } : null;
}

